import * as THREE from 'three'
import { GSplatBoxShaderEffect } from '@/plugins/gsplat/effects/GSplatBoxShaderEffect'
import {
  GSPLAT_CHAOTIC_PARTICLE_SCATTER_DEFAULTS,
  GSplatChaoticParticleScatterEffect,
} from '@/plugins/gsplat/effects/GSplatChaoticParticleScatterEffect'
import { GSplatCropShaderEffect } from '@/plugins/gsplat/effects/GSplatCropShaderEffect'
import { GSplatDissolveDriftEffect } from '@/plugins/gsplat/effects/GSplatDissolveDriftEffect'
import { GSplatFractalOrbitParticleEffect } from '@/plugins/gsplat/effects/GSplatFractalOrbitParticleEffect'
import { GSplatLayeredPrintParticleEffect } from '@/plugins/gsplat/effects/GSplatLayeredPrintParticleEffect'
import { GSplatObbShaderEffect } from '@/plugins/gsplat/effects/GSplatObbShaderEffect'
import {
  GSPLAT_PARTICLE_SIZE_MODES,
  type GSplatParticleLifecycleEffect,
  type GSplatParticleSizeMode,
} from '@/plugins/gsplat/effects/GSplatParticleLifecycleEffect'
import { GSplatRevealGridEruption } from '@/plugins/gsplat/effects/GSplatRevealGridEruption'
import { GSplatRevealMagicEffect } from '@/plugins/gsplat/effects/GSplatRevealMagicEffect'
import { GSplatRevealRadial } from '@/plugins/gsplat/effects/GSplatRevealRadial'
import { GSplatRevealRain } from '@/plugins/gsplat/effects/GSplatRevealRain'
import { GSplatRevealSpreadEffect } from '@/plugins/gsplat/effects/GSplatRevealSpreadEffect'
import { GSplatRevealTwisterEffect } from '@/plugins/gsplat/effects/GSplatRevealTwisterEffect'
import { GSplatRevealUnrollEffect } from '@/plugins/gsplat/effects/GSplatRevealUnrollEffect'
import { GSplatVisualDeepMeditationEffect } from '@/plugins/gsplat/effects/GSplatVisualDeepMeditationEffect'
import { GSplatVisualDisintegrateEffect } from '@/plugins/gsplat/effects/GSplatVisualDisintegrateEffect'
import { GSplatVisualElectronicEffect } from '@/plugins/gsplat/effects/GSplatVisualElectronicEffect'
import { GSplatVisualFlareEffect } from '@/plugins/gsplat/effects/GSplatVisualFlareEffect'
import { GSplatVisualWavesEffect } from '@/plugins/gsplat/effects/GSplatVisualWavesEffect'
import type { GSplatParticlePhaseWeights } from '@/plugins/gsplat/effects/particleLifecycle'
import {
  GSPLAT_LAYERED_PRINT_DIRECTIONS,
  GSPLAT_PARTICLE_MOTION_DEFAULTS,
  GSPLAT_PARTICLE_MOTION_LIMITS,
  GSPLAT_PARTICLE_MOTION_TYPES,
  type GSplatParticleMotion,
} from '@/plugins/gsplat/effects/particleMotion'
import type { GSplatShaderEffectLike } from '@/shared/types/gsplat'

export const GSPLAT_EFFECT_TEMPLATE_KIND = 'share3d.gsplat-effect-template'
export const GSPLAT_EFFECT_TEMPLATE_VERSION = 1

export const GSPLAT_EFFECT_TEMPLATE_KEYS = [
  'box-reveal',
  'box-hide',
  'box-tint',
  'box-untint',
  'box-crop',
  'reveal-radial',
  'reveal-rain',
  'reveal-grid',
  'obb-double-wave',
  'visual-electronic',
  'visual-deep-meditation',
  'visual-waves',
  'visual-flare',
  'visual-disintegrate',
  'reveal-magic',
  'reveal-spread',
  'reveal-unroll',
  'reveal-twister',
  'particle-scatter-reassemble',
  'dissolve-drift',
] as const

export const GSPLAT_EFFECT_TEMPLATE_BOX_DIRECTIONS = [
  'z-up',
  'z-down',
  'x-plus',
  'x-minus',
  'y-plus',
  'y-minus',
] as const

export type GSplatEffectTemplateKey = (typeof GSPLAT_EFFECT_TEMPLATE_KEYS)[number]
export type GSplatEffectTemplateBoxDirection =
  (typeof GSPLAT_EFFECT_TEMPLATE_BOX_DIRECTIONS)[number]

export type GSplatEffectTemplateBoxScanKey = 'box-reveal' | 'box-hide' | 'box-tint' | 'box-untint'

export type GSplatEffectTemplateRevealWaveKey = 'reveal-radial' | 'obb-double-wave'
export type GSplatEffectTemplateVisualShowcaseKey =
  | 'visual-electronic'
  | 'visual-deep-meditation'
  | 'visual-waves'
  | 'visual-flare'
  | 'visual-disintegrate'

export type GSplatEffectTemplateRevealShowcaseKey =
  | 'reveal-magic'
  | 'reveal-spread'
  | 'reveal-unroll'
  | 'reveal-twister'

export type GSplatEffectTemplateDissolveKey = 'dissolve-drift'
export type GSplatEffectTemplateParticleKey = 'particle-scatter-reassemble'

export type GSplatEffectTemplateVector3Like =
  | readonly [number, number, number]
  | { x: number; y: number; z: number }

export interface GSplatEffectTemplateBox3Like {
  min: GSplatEffectTemplateVector3Like
  max: GSplatEffectTemplateVector3Like
}

export interface GSplatEffectTemplateColorControl {
  color: string
  intensity: number
}

export interface GSplatEffectTemplateBoxScanParams {
  size: number
  direction: GSplatEffectTemplateBoxDirection
  duration: number
  tintColor: string
  edgeColor: string
}

export interface GSplatEffectTemplateBoxCropParams {
  size: number
  edgeScaleFactor: number
}

export interface GSplatEffectTemplateRevealWaveParams {
  duration: number
  dotTint: GSplatEffectTemplateColorControl
  waveTint: GSplatEffectTemplateColorControl
  intensity: number
}

export interface GSplatEffectTemplateRevealWaveManualParams {
  speed: number
  acceleration: number
  delay: number
  dotTint: GSplatEffectTemplateColorControl
  waveTint: GSplatEffectTemplateColorControl
  oscillationIntensity: number
  bandWidth: number
}

export interface GSplatEffectTemplateRainParams {
  duration: number
  fallTint: GSplatEffectTemplateColorControl
  fallTintIntensity: number
  hitTint: GSplatEffectTemplateColorControl
  intensity: number
}

export interface GSplatEffectTemplateRainManualParams {
  distance: number
  speed: number
  acceleration: number
  flightTime: number
  rainSize: number
  rotation: number
  fallTint: GSplatEffectTemplateColorControl
  fallTintIntensity: number
  hitTint: GSplatEffectTemplateColorControl
  hitDuration: number
}

export interface GSplatEffectTemplateGridParams {
  duration: number
  moveTint: GSplatEffectTemplateColorControl
  moveTintIntensity: number
  landTint: GSplatEffectTemplateColorControl
  intensity: number
}

export interface GSplatEffectTemplateGridManualParams {
  blockSize: number
  delay: number
  duration: number
  dotSize: number
  moveTint: GSplatEffectTemplateColorControl
  moveTintIntensity: number
  landTint: GSplatEffectTemplateColorControl
  landDuration: number
}

export interface GSplatEffectTemplateVisualShowcaseParams {
  intensity: number
  timeScale: number
  swayAmplitude?: number
  swaySpeed?: number
  flickerColor?: GSplatEffectTemplateColorControl
}

export interface GSplatEffectTemplateRevealShowcaseParams {
  timeScale: number
  motionScale: number
  duration: number
}

export interface GSplatEffectTemplateDissolveDriftParams {
  speed: number
  motionScale: number
}

export interface GSplatEffectTemplateParticleStyleParams {
  sizeMode: GSplatParticleSizeMode
  targetSize: number
  targetSizeRatio: number
  opacity: number
  highlightStrength: number
  glowStrength: number
  glowRadius: number
}

export interface GSplatEffectTemplateParticleJitterParams {
  amplitude: number
  speed: number
}

export interface GSplatEffectTemplateParticleTimingParams {
  duration: number
  preScatterHoldDuration: number
  peakHoldDuration: number
  phaseWeights: GSplatParticlePhaseWeights
}

export interface GSplatEffectTemplateParticleRandomParams {
  seed: number
}

export interface GSplatEffectTemplateParticleScatterParams {
  motion: GSplatParticleMotion
  style: GSplatEffectTemplateParticleStyleParams
  jitter: GSplatEffectTemplateParticleJitterParams
  timing: GSplatEffectTemplateParticleTimingParams
  random: GSplatEffectTemplateParticleRandomParams
}

export type GSplatEffectTemplateParams =
  | GSplatEffectTemplateBoxScanParams
  | GSplatEffectTemplateBoxCropParams
  | GSplatEffectTemplateRevealWaveParams
  | GSplatEffectTemplateRevealWaveManualParams
  | GSplatEffectTemplateRainParams
  | GSplatEffectTemplateRainManualParams
  | GSplatEffectTemplateGridParams
  | GSplatEffectTemplateGridManualParams
  | GSplatEffectTemplateVisualShowcaseParams
  | GSplatEffectTemplateRevealShowcaseParams
  | GSplatEffectTemplateParticleScatterParams
  | GSplatEffectTemplateDissolveDriftParams

export type GSplatEffectTemplate =
  | GSplatEffectTemplateBoxScan
  | GSplatEffectTemplateBoxCrop
  | GSplatEffectTemplateRevealWave
  | GSplatEffectTemplateRain
  | GSplatEffectTemplateGrid
  | GSplatEffectTemplateVisualShowcase
  | GSplatEffectTemplateRevealShowcase
  | GSplatEffectTemplateParticleScatter
  | GSplatEffectTemplateDissolveDrift

export interface GSplatEffectTemplateBase {
  kind: typeof GSPLAT_EFFECT_TEMPLATE_KIND
  version: typeof GSPLAT_EFFECT_TEMPLATE_VERSION
  id: string
  name: string
  description?: string
  adaptive?: boolean
}

export interface GSplatEffectTemplateBoxScan extends GSplatEffectTemplateBase {
  effectKey: GSplatEffectTemplateBoxScanKey
  params: GSplatEffectTemplateBoxScanParams
}

export interface GSplatEffectTemplateBoxCrop extends GSplatEffectTemplateBase {
  effectKey: 'box-crop'
  params: GSplatEffectTemplateBoxCropParams
}

export interface GSplatEffectTemplateRevealWaveAdaptive extends GSplatEffectTemplateBase {
  effectKey: GSplatEffectTemplateRevealWaveKey
  adaptive: true
  params: GSplatEffectTemplateRevealWaveParams
}

export interface GSplatEffectTemplateRevealWaveManual extends GSplatEffectTemplateBase {
  effectKey: GSplatEffectTemplateRevealWaveKey
  adaptive: false
  params: GSplatEffectTemplateRevealWaveManualParams
}

export type GSplatEffectTemplateRevealWave =
  | GSplatEffectTemplateRevealWaveAdaptive
  | GSplatEffectTemplateRevealWaveManual

export interface GSplatEffectTemplateRainAdaptive extends GSplatEffectTemplateBase {
  effectKey: 'reveal-rain'
  adaptive: true
  params: GSplatEffectTemplateRainParams
}

export interface GSplatEffectTemplateRainManual extends GSplatEffectTemplateBase {
  effectKey: 'reveal-rain'
  adaptive: false
  params: GSplatEffectTemplateRainManualParams
}

export type GSplatEffectTemplateRain =
  | GSplatEffectTemplateRainAdaptive
  | GSplatEffectTemplateRainManual

export interface GSplatEffectTemplateGridAdaptive extends GSplatEffectTemplateBase {
  effectKey: 'reveal-grid'
  adaptive: true
  params: GSplatEffectTemplateGridParams
}

export interface GSplatEffectTemplateGridManual extends GSplatEffectTemplateBase {
  effectKey: 'reveal-grid'
  adaptive: false
  params: GSplatEffectTemplateGridManualParams
}

export type GSplatEffectTemplateGrid =
  | GSplatEffectTemplateGridAdaptive
  | GSplatEffectTemplateGridManual

export interface GSplatEffectTemplateVisualShowcase extends GSplatEffectTemplateBase {
  effectKey: GSplatEffectTemplateVisualShowcaseKey
  params: GSplatEffectTemplateVisualShowcaseParams
}

export interface GSplatEffectTemplateRevealShowcase extends GSplatEffectTemplateBase {
  effectKey: GSplatEffectTemplateRevealShowcaseKey
  params: GSplatEffectTemplateRevealShowcaseParams
}

export interface GSplatEffectTemplateDissolveDrift extends GSplatEffectTemplateBase {
  effectKey: 'dissolve-drift'
  params: GSplatEffectTemplateDissolveDriftParams
}

export interface GSplatEffectTemplateParticleScatter extends GSplatEffectTemplateBase {
  effectKey: GSplatEffectTemplateParticleKey
  params: GSplatEffectTemplateParticleScatterParams
}

export interface GSplatEffectTemplateCameraContext {
  position: GSplatEffectTemplateVector3Like
  target: GSplatEffectTemplateVector3Like
  up: GSplatEffectTemplateVector3Like
}

export interface GSplatEffectTemplateRuntimeContext {
  effectSpace: 'effect-local'
  center: GSplatEffectTemplateVector3Like
  effectBounds?: GSplatEffectTemplateBox3Like | null
  sceneBounds?: GSplatEffectTemplateBox3Like | null
  sceneRadius?: number
  defaultSceneRadius?: number
  minRevealRadius?: number
  camera?: GSplatEffectTemplateCameraContext
}

export interface GSplatEffectTemplateDiagnostics {
  effectKey: GSplatEffectTemplateKey
  sceneRadius: number
  usedEffectBounds: boolean
  usedSceneBounds: boolean
}

export interface GSplatEffectTemplateResult {
  effect: GSplatShaderEffectLike
  diagnostics: GSplatEffectTemplateDiagnostics
}

export class GSplatEffectTemplateError extends Error {
  constructor(message: string) {
    super(message)
    this.name = 'GSplatEffectTemplateError'
  }
}

const TEMPLATE_TOP_LEVEL_KEYS = [
  'kind',
  'version',
  'id',
  'name',
  'description',
  'effectKey',
  'adaptive',
  'params',
] as const

const BOX_SCAN_PARAM_KEYS = ['size', 'direction', 'duration', 'tintColor', 'edgeColor'] as const
const BOX_CROP_PARAM_KEYS = ['size', 'edgeScaleFactor'] as const
const REVEAL_WAVE_PARAM_KEYS = ['duration', 'dotTint', 'waveTint', 'intensity'] as const
export const GSPLAT_EFFECT_TEMPLATE_REVEAL_WAVE_MANUAL_PARAM_KEYS = [
  'speed',
  'acceleration',
  'delay',
  'dotTint',
  'waveTint',
  'oscillationIntensity',
  'bandWidth',
] as const
const RAIN_PARAM_KEYS = [
  'duration',
  'fallTint',
  'fallTintIntensity',
  'hitTint',
  'intensity',
] as const
export const GSPLAT_EFFECT_TEMPLATE_RAIN_MANUAL_PARAM_KEYS = [
  'distance',
  'speed',
  'acceleration',
  'flightTime',
  'rainSize',
  'rotation',
  'fallTint',
  'fallTintIntensity',
  'hitTint',
  'hitDuration',
] as const
const GRID_PARAM_KEYS = [
  'duration',
  'moveTint',
  'moveTintIntensity',
  'landTint',
  'intensity',
] as const
export const GSPLAT_EFFECT_TEMPLATE_GRID_MANUAL_PARAM_KEYS = [
  'blockSize',
  'delay',
  'duration',
  'dotSize',
  'moveTint',
  'moveTintIntensity',
  'landTint',
  'landDuration',
] as const
const VISUAL_SHOWCASE_PARAM_KEYS = [
  'intensity',
  'timeScale',
  'swayAmplitude',
  'swaySpeed',
  'flickerColor',
] as const
const REVEAL_SHOWCASE_PARAM_KEYS = ['timeScale', 'motionScale', 'duration'] as const
export const GSPLAT_EFFECT_TEMPLATE_PARTICLE_SCATTER_PARAM_KEYS = [
  'motion',
  'style',
  'jitter',
  'timing',
  'random',
] as const
const PARTICLE_MOTION_KEYS = ['type', 'params'] as const
const PARTICLE_STYLE_KEYS = [
  'sizeMode',
  'targetSize',
  'targetSizeRatio',
  'opacity',
  'highlightStrength',
  'glowStrength',
  'glowRadius',
] as const
const PARTICLE_JITTER_KEYS = ['amplitude', 'speed'] as const
const PARTICLE_TIMING_KEYS = [
  'duration',
  'preScatterHoldDuration',
  'peakHoldDuration',
  'phaseWeights',
] as const
const PARTICLE_RANDOM_KEYS = ['seed'] as const
const CHAOTIC_PARTICLE_MOTION_PARAM_KEYS = ['scatterScale'] as const
const LAYERED_PRINT_PARTICLE_MOTION_PARAM_KEYS = [
  'layerCount',
  'bandWidth',
  'liftScale',
  'direction',
] as const
const FRACTAL_ORBIT_PARTICLE_MOTION_PARAM_KEYS = ['orbitScale', 'frequency', 'sweepWidth'] as const
export const GSPLAT_EFFECT_TEMPLATE_PARTICLE_PHASE_WEIGHT_KEYS = [
  'particleize',
  'scatter',
  'gather',
  'restore',
] as const
const DISSOLVE_DRIFT_PARAM_KEYS = ['speed', 'motionScale'] as const
const COLOR_CONTROL_KEYS = ['color', 'intensity'] as const

const EFFECT_KEY_SET = new Set<string>(GSPLAT_EFFECT_TEMPLATE_KEYS)

const NUMERIC_LIMITS = {
  colorIntensity: { min: 0, max: 5 },
  boxSize: { min: 0.5, max: 1000 },
  boxDuration: { min: 0.5, max: 12 },
  edgeScaleFactor: { min: 0, max: 2 },
  revealDuration: { min: 1.2, max: 20 },
  unitIntensity: { min: 0, max: 1 },
  revealIntensity: { min: 0, max: 3 },
  revealSpeed: { min: 0, max: 20 },
  revealAcceleration: { min: 0, max: 10 },
  revealDelay: { min: 0, max: 10 },
  revealBandWidth: { min: 0.1, max: 5 },
  rainDistance: { min: 0, max: 120 },
  rainFlightTime: { min: 0.1, max: 8 },
  rainSize: { min: 0.001, max: 0.2 },
  rainRotation: { min: 0, max: 2 },
  rainHitDuration: { min: 0.1, max: 4 },
  gridBlockSize: { min: 0.1, max: 10 },
  gridDelay: { min: 0, max: 2 },
  gridDuration: { min: 0.1, max: 5 },
  gridDotSize: { min: 0.001, max: 0.2 },
  gridLandDuration: { min: 0.1, max: 4 },
  visualIntensity: { min: 0, max: 3 },
  swayAmplitude: { min: 0, max: 1 },
  swaySpeed: { min: 0, max: 8 },
  timeScale: { min: 0, max: 4 },
  motionScale: { min: 0, max: 5 },
  showcaseDuration: { min: 0.5, max: 20 },
  dissolveSpeed: { min: 0.05, max: 5 },
} as const

export const GSPLAT_PARTICLE_SCATTER_TEMPLATE_LIMITS = {
  scatterScale: GSPLAT_PARTICLE_MOTION_LIMITS.chaotic.scatterScale,
  layerCount: GSPLAT_PARTICLE_MOTION_LIMITS['layered-print'].layerCount,
  printBandWidth: GSPLAT_PARTICLE_MOTION_LIMITS['layered-print'].bandWidth,
  liftScale: GSPLAT_PARTICLE_MOTION_LIMITS['layered-print'].liftScale,
  orbitScale: GSPLAT_PARTICLE_MOTION_LIMITS['fractal-orbit'].orbitScale,
  orbitFrequency: GSPLAT_PARTICLE_MOTION_LIMITS['fractal-orbit'].frequency,
  orbitSweepWidth: GSPLAT_PARTICLE_MOTION_LIMITS['fractal-orbit'].sweepWidth,
  particleTargetSize: { min: 0, max: 0.01 },
  particleTargetSizeRatio: { min: 0, max: 0.001 },
  particleOpacity: { min: 0.05, max: 1 },
  particleHighlightStrength: { min: 0, max: 3 },
  particleGlowStrength: { min: 0, max: 3 },
  particleGlowRadius: { min: 0, max: 1 },
  particleJitterAmplitude: { min: 0, max: 0.005 },
  particleJitterSpeed: { min: 0, max: 4 },
  phaseWeight: { min: 0, max: 10 },
  seed: { min: 0, max: 65535 },
} as const

const DEFAULT_COLOR_CONTROLS = {
  cyan: { color: '#00ffff', intensity: 1 },
  orange: { color: '#ff8000', intensity: 1 },
  redStrong: { color: '#ff0000', intensity: 2 },
  magenta: { color: '#ff00ff', intensity: 1 },
  yellowStrong: { color: '#ffff00', intensity: 2 },
} as const satisfies Record<string, GSplatEffectTemplateColorControl>

const BOX_EDGE_TINT_INTENSITY = 5
const DEFAULT_SCENE_RADIUS = 32
const DEFAULT_MIN_REVEAL_RADIUS = 10
const MAX_REVEAL_RADIUS = NUMERIC_LIMITS.boxSize.max
const EPSILON = 1e-8
const DEFAULT_REVEAL_DURATION = 4
const DEFAULT_GRID_REVEAL_DURATION = 3
const DOUBLE_WAVE_DELAY_RATIO = 0.25
const DOUBLE_WAVE_MAX_DELAY = 1.2
const RAIN_TAIL_RATIO = 0.16
const RAIN_FLIGHT_RATIO = 0.22
const RAIN_JITTER_RATIO = 0.18
const RAIN_MAX_FLIGHT_SCALE = 1.18
const GRID_LAND_TAIL_RATIO = 0.16
const GRID_MOVE_RATIO = 0.34
const PLAYCANVAS_REFERENCE_RADIUS = 10
const MAGIC_REFERENCE_RADIUS = 10
const UNROLL_REFERENCE_RADIUS = 1
const GRID_REFERENCE_BLOCK_SIZE = 2
const GRID_REFERENCE_DOT_SIZE = 0.01
const DEFAULT_FORWARD = new THREE.Vector3(0, 1, 0)
const DEFAULT_UP = new THREE.Vector3(0, 0, 1)

const BOX_DIRECTION_VECTORS: Record<GSplatEffectTemplateBoxDirection, [number, number, number]> = {
  'z-up': [0, 0, 1],
  'z-down': [0, 0, -1],
  'x-plus': [1, 0, 0],
  'x-minus': [-1, 0, 0],
  'y-plus': [0, 1, 0],
  'y-minus': [0, -1, 0],
}

type NumericLimit = {
  min: number
  max: number
}

interface NormalizedRuntimeContext {
  center: THREE.Vector3
  effectBounds: THREE.Box3 | null
  sceneBounds: THREE.Box3 | null
  sceneRadius: number | null
  defaultSceneRadius: number
  minRevealRadius: number
  camera: {
    position: THREE.Vector3
    target: THREE.Vector3
    up: THREE.Vector3
  } | null
}

interface RevealAdaptiveScale {
  modelRadius: number
  viewRadius: number
  revealRadius: number
  detailScale: number
}

interface DoubleWaveTiming {
  speed: number
  delay: number
}

interface RainTiming {
  speed: number
  acceleration: number
  spreadTime: number
  spreadCurve: number
  waveRadius: number
  flightTime: number
  dropJitter: number
  hitDuration: number
}

interface GridTiming {
  delay: number
  blockMoveDuration: number
  landDuration: number
}

export function parseGSplatEffectTemplate(source: unknown): GSplatEffectTemplate {
  const template = requireRecord(source, 'GSplat effect template must be an object')
  assertNoUnknownKeys(template, TEMPLATE_TOP_LEVEL_KEYS, 'GSplat effect template')

  if (template.kind !== GSPLAT_EFFECT_TEMPLATE_KIND) {
    throw new GSplatEffectTemplateError(
      `Unsupported GSplat effect template kind: ${String(template.kind)}`
    )
  }
  if (template.version !== GSPLAT_EFFECT_TEMPLATE_VERSION) {
    throw new GSplatEffectTemplateError(
      `Unsupported GSplat effect template version: ${String(template.version)}`
    )
  }

  const id = readNonEmptyString(template.id, 'id')
  const name = readNonEmptyString(template.name, 'name')
  const description =
    template.description === undefined ? undefined : readString(template.description, 'description')
  const effectKey = readEffectKey(template.effectKey)
  const adaptive = readAdaptive(template.adaptive, effectKey)
  const params = parseParams(requireRecord(template.params, 'params'), effectKey, adaptive)
  const base = {
    kind: GSPLAT_EFFECT_TEMPLATE_KIND,
    version: GSPLAT_EFFECT_TEMPLATE_VERSION,
    id,
    name,
    ...(description !== undefined ? { description } : {}),
    ...(isAdaptiveEffectKey(effectKey) ? { adaptive } : {}),
  }

  return { ...base, effectKey, params } as GSplatEffectTemplate
}

export function createGSplatEffectFromTemplate(
  source: GSplatEffectTemplate | unknown,
  context: GSplatEffectTemplateRuntimeContext
): GSplatEffectTemplateResult {
  const template = parseGSplatEffectTemplate(source)
  const runtime = normalizeRuntimeContext(context)
  const diagnostics = createDiagnostics(template.effectKey, runtime)

  switch (template.effectKey) {
    case 'box-reveal':
    case 'box-hide':
    case 'box-tint':
    case 'box-untint':
      return {
        effect: createBoxScanEffect(template, runtime),
        diagnostics,
      }
    case 'box-crop':
      return {
        effect: createBoxCropEffect(template, runtime),
        diagnostics,
      }
    case 'reveal-radial':
      return {
        effect: createRadialEffect(template, runtime, diagnostics.sceneRadius),
        diagnostics,
      }
    case 'reveal-rain':
      return {
        effect: createRainEffect(template, runtime, diagnostics.sceneRadius),
        diagnostics,
      }
    case 'reveal-grid':
      return {
        effect: createGridEffect(template, runtime, diagnostics.sceneRadius),
        diagnostics,
      }
    case 'obb-double-wave':
      return {
        effect: createObbDoubleWaveEffect(template, runtime),
        diagnostics,
      }
    case 'visual-electronic':
    case 'visual-deep-meditation':
    case 'visual-waves':
    case 'visual-flare':
    case 'visual-disintegrate':
      return {
        effect: createVisualShowcaseEffect(template, runtime),
        diagnostics,
      }
    case 'reveal-magic':
    case 'reveal-spread':
    case 'reveal-unroll':
    case 'reveal-twister':
      return {
        effect: createRevealShowcaseEffect(template, runtime, diagnostics.sceneRadius),
        diagnostics,
      }
    case 'particle-scatter-reassemble':
      return {
        effect: createParticleScatterEffect(template, runtime, diagnostics.sceneRadius),
        diagnostics,
      }
    case 'dissolve-drift':
      return {
        effect: createDissolveDriftEffect(template, runtime),
        diagnostics,
      }
  }
}

function parseParams(
  params: Record<string, unknown>,
  effectKey: GSplatEffectTemplateKey,
  adaptive: boolean
): GSplatEffectTemplateParams {
  switch (effectKey) {
    case 'box-reveal':
    case 'box-hide':
    case 'box-tint':
    case 'box-untint':
      return parseBoxScanParams(params)
    case 'box-crop':
      return parseBoxCropParams(params)
    case 'reveal-radial':
    case 'obb-double-wave':
      return adaptive ? parseRevealWaveParams(params) : parseRevealWaveManualParams(params)
    case 'reveal-rain':
      return adaptive ? parseRainParams(params) : parseRainManualParams(params)
    case 'reveal-grid':
      return adaptive ? parseGridParams(params) : parseGridManualParams(params)
    case 'visual-electronic':
    case 'visual-deep-meditation':
    case 'visual-waves':
    case 'visual-flare':
    case 'visual-disintegrate':
      return parseVisualShowcaseParams(params)
    case 'reveal-magic':
    case 'reveal-spread':
    case 'reveal-unroll':
    case 'reveal-twister':
      return parseRevealShowcaseParams(params)
    case 'particle-scatter-reassemble':
      return parseParticleScatterParams(params)
    case 'dissolve-drift':
      return parseDissolveDriftParams(params)
  }
}

function readAdaptive(value: unknown, effectKey: GSplatEffectTemplateKey): boolean {
  if (value === undefined) {
    return true
  }
  if (!isAdaptiveEffectKey(effectKey)) {
    throw new GSplatEffectTemplateError(`adaptive is not supported for effectKey: ${effectKey}`)
  }
  if (typeof value !== 'boolean') {
    throw new GSplatEffectTemplateError('adaptive must be a boolean')
  }
  return value
}

function isAdaptiveEffectKey(effectKey: GSplatEffectTemplateKey): boolean {
  return (
    effectKey === 'reveal-radial' ||
    effectKey === 'reveal-rain' ||
    effectKey === 'reveal-grid' ||
    effectKey === 'obb-double-wave'
  )
}

function parseBoxScanParams(params: Record<string, unknown>): GSplatEffectTemplateBoxScanParams {
  assertNoUnknownKeys(params, BOX_SCAN_PARAM_KEYS, 'params')

  return {
    size: readNumber(params.size, NUMERIC_LIMITS.boxSize, 'params.size', 4),
    direction: readEnum(
      params.direction,
      GSPLAT_EFFECT_TEMPLATE_BOX_DIRECTIONS,
      'params.direction',
      'z-up'
    ),
    duration: readNumber(params.duration, NUMERIC_LIMITS.boxDuration, 'params.duration', 3.5),
    tintColor: readHexColor(params.tintColor, 'params.tintColor', '#00ffff'),
    edgeColor: readHexColor(params.edgeColor, 'params.edgeColor', '#ffff00'),
  }
}

function parseBoxCropParams(params: Record<string, unknown>): GSplatEffectTemplateBoxCropParams {
  assertNoUnknownKeys(params, BOX_CROP_PARAM_KEYS, 'params')

  return {
    size: readNumber(params.size, NUMERIC_LIMITS.boxSize, 'params.size', 4),
    edgeScaleFactor: readNumber(
      params.edgeScaleFactor,
      NUMERIC_LIMITS.edgeScaleFactor,
      'params.edgeScaleFactor',
      0.45
    ),
  }
}

function parseRevealWaveParams(
  params: Record<string, unknown>
): GSplatEffectTemplateRevealWaveParams {
  assertNoUnknownKeys(params, REVEAL_WAVE_PARAM_KEYS, 'params')

  const parsed = {
    duration: readNumber(
      params.duration,
      NUMERIC_LIMITS.revealDuration,
      'params.duration',
      DEFAULT_REVEAL_DURATION
    ),
    dotTint: readColorControl(params.dotTint, 'params.dotTint', DEFAULT_COLOR_CONTROLS.cyan),
    waveTint: readColorControl(params.waveTint, 'params.waveTint', DEFAULT_COLOR_CONTROLS.orange),
    intensity: readNumber(params.intensity, NUMERIC_LIMITS.revealIntensity, 'params.intensity', 1),
  }

  return parsed
}

function parseRevealWaveManualParams(
  params: Record<string, unknown>
): GSplatEffectTemplateRevealWaveManualParams {
  assertNoUnknownKeys(params, GSPLAT_EFFECT_TEMPLATE_REVEAL_WAVE_MANUAL_PARAM_KEYS, 'params')

  return {
    speed: readNumber(params.speed, NUMERIC_LIMITS.revealSpeed, 'params.speed', 3.5),
    acceleration: readNumber(
      params.acceleration,
      NUMERIC_LIMITS.revealAcceleration,
      'params.acceleration',
      0
    ),
    delay: readNumber(params.delay, NUMERIC_LIMITS.revealDelay, 'params.delay', 0.15),
    dotTint: readColorControl(params.dotTint, 'params.dotTint', DEFAULT_COLOR_CONTROLS.cyan),
    waveTint: readColorControl(params.waveTint, 'params.waveTint', DEFAULT_COLOR_CONTROLS.orange),
    oscillationIntensity: readNumber(
      params.oscillationIntensity,
      NUMERIC_LIMITS.unitIntensity,
      'params.oscillationIntensity',
      0.2
    ),
    bandWidth: readNumber(params.bandWidth, NUMERIC_LIMITS.revealBandWidth, 'params.bandWidth', 1),
  }
}

function parseRainParams(params: Record<string, unknown>): GSplatEffectTemplateRainParams {
  assertNoUnknownKeys(params, RAIN_PARAM_KEYS, 'params')

  const parsed = {
    duration: readNumber(
      params.duration,
      NUMERIC_LIMITS.revealDuration,
      'params.duration',
      DEFAULT_REVEAL_DURATION
    ),
    fallTint: readColorControl(params.fallTint, 'params.fallTint', DEFAULT_COLOR_CONTROLS.cyan),
    fallTintIntensity: readNumber(
      params.fallTintIntensity,
      NUMERIC_LIMITS.unitIntensity,
      'params.fallTintIntensity',
      0.2
    ),
    hitTint: readColorControl(params.hitTint, 'params.hitTint', DEFAULT_COLOR_CONTROLS.redStrong),
    intensity: readNumber(params.intensity, NUMERIC_LIMITS.revealIntensity, 'params.intensity', 1),
  }

  return parsed
}

function parseRainManualParams(
  params: Record<string, unknown>
): GSplatEffectTemplateRainManualParams {
  assertNoUnknownKeys(params, GSPLAT_EFFECT_TEMPLATE_RAIN_MANUAL_PARAM_KEYS, 'params')

  return {
    distance: readNumber(params.distance, NUMERIC_LIMITS.rainDistance, 'params.distance', 8),
    speed: readNumber(params.speed, NUMERIC_LIMITS.revealSpeed, 'params.speed', 8),
    acceleration: readNumber(
      params.acceleration,
      NUMERIC_LIMITS.revealAcceleration,
      'params.acceleration',
      0
    ),
    flightTime: readNumber(
      params.flightTime,
      NUMERIC_LIMITS.rainFlightTime,
      'params.flightTime',
      2
    ),
    rainSize: readNumber(params.rainSize, NUMERIC_LIMITS.rainSize, 'params.rainSize', 0.015),
    rotation: readNumber(params.rotation, NUMERIC_LIMITS.rainRotation, 'params.rotation', 0.9),
    fallTint: readColorControl(params.fallTint, 'params.fallTint', DEFAULT_COLOR_CONTROLS.cyan),
    fallTintIntensity: readNumber(
      params.fallTintIntensity,
      NUMERIC_LIMITS.unitIntensity,
      'params.fallTintIntensity',
      0.2
    ),
    hitTint: readColorControl(params.hitTint, 'params.hitTint', DEFAULT_COLOR_CONTROLS.redStrong),
    hitDuration: readNumber(
      params.hitDuration,
      NUMERIC_LIMITS.rainHitDuration,
      'params.hitDuration',
      0.5
    ),
  }
}

function parseGridParams(params: Record<string, unknown>): GSplatEffectTemplateGridParams {
  assertNoUnknownKeys(params, GRID_PARAM_KEYS, 'params')

  return {
    duration: readNumber(
      params.duration,
      NUMERIC_LIMITS.revealDuration,
      'params.duration',
      DEFAULT_GRID_REVEAL_DURATION
    ),
    moveTint: readColorControl(params.moveTint, 'params.moveTint', DEFAULT_COLOR_CONTROLS.magenta),
    moveTintIntensity: readNumber(
      params.moveTintIntensity,
      NUMERIC_LIMITS.unitIntensity,
      'params.moveTintIntensity',
      0.2
    ),
    landTint: readColorControl(
      params.landTint,
      'params.landTint',
      DEFAULT_COLOR_CONTROLS.yellowStrong
    ),
    intensity: readNumber(params.intensity, NUMERIC_LIMITS.revealIntensity, 'params.intensity', 1),
  }
}

function parseGridManualParams(
  params: Record<string, unknown>
): GSplatEffectTemplateGridManualParams {
  assertNoUnknownKeys(params, GSPLAT_EFFECT_TEMPLATE_GRID_MANUAL_PARAM_KEYS, 'params')

  return {
    blockSize: readNumber(
      params.blockSize,
      NUMERIC_LIMITS.gridBlockSize,
      'params.blockSize',
      GRID_REFERENCE_BLOCK_SIZE
    ),
    delay: readNumber(params.delay, NUMERIC_LIMITS.gridDelay, 'params.delay', 0.04),
    duration: readNumber(params.duration, NUMERIC_LIMITS.gridDuration, 'params.duration', 1),
    dotSize: readNumber(
      params.dotSize,
      NUMERIC_LIMITS.gridDotSize,
      'params.dotSize',
      GRID_REFERENCE_DOT_SIZE
    ),
    moveTint: readColorControl(params.moveTint, 'params.moveTint', DEFAULT_COLOR_CONTROLS.magenta),
    moveTintIntensity: readNumber(
      params.moveTintIntensity,
      NUMERIC_LIMITS.unitIntensity,
      'params.moveTintIntensity',
      0.2
    ),
    landTint: readColorControl(
      params.landTint,
      'params.landTint',
      DEFAULT_COLOR_CONTROLS.yellowStrong
    ),
    landDuration: readNumber(
      params.landDuration,
      NUMERIC_LIMITS.gridLandDuration,
      'params.landDuration',
      0.6
    ),
  }
}

function parseVisualShowcaseParams(
  params: Record<string, unknown>
): GSplatEffectTemplateVisualShowcaseParams {
  assertNoUnknownKeys(params, VISUAL_SHOWCASE_PARAM_KEYS, 'params')

  return {
    intensity: readNumber(params.intensity, NUMERIC_LIMITS.visualIntensity, 'params.intensity', 1),
    timeScale: readNumber(params.timeScale, NUMERIC_LIMITS.timeScale, 'params.timeScale', 1),
    swayAmplitude: readNumber(
      params.swayAmplitude,
      NUMERIC_LIMITS.swayAmplitude,
      'params.swayAmplitude',
      0.2
    ),
    swaySpeed: readNumber(params.swaySpeed, NUMERIC_LIMITS.swaySpeed, 'params.swaySpeed', 2),
    flickerColor: readColorControl(params.flickerColor, 'params.flickerColor', {
      color: '#0080b3',
      intensity: 1,
    }),
  }
}

function parseRevealShowcaseParams(
  params: Record<string, unknown>
): GSplatEffectTemplateRevealShowcaseParams {
  assertNoUnknownKeys(params, REVEAL_SHOWCASE_PARAM_KEYS, 'params')

  return {
    timeScale: readNumber(params.timeScale, NUMERIC_LIMITS.timeScale, 'params.timeScale', 1),
    motionScale: readNumber(
      params.motionScale,
      NUMERIC_LIMITS.motionScale,
      'params.motionScale',
      1
    ),
    duration: readNumber(params.duration, NUMERIC_LIMITS.showcaseDuration, 'params.duration', 6),
  }
}

function parseParticleScatterParams(
  params: Record<string, unknown>
): GSplatEffectTemplateParticleScatterParams {
  assertNoUnknownKeys(params, GSPLAT_EFFECT_TEMPLATE_PARTICLE_SCATTER_PARAM_KEYS, 'params')

  const style = parseParticleStyle(params.style)
  const jitter = parseParticleJitter(params.jitter)
  const timing = parseParticleTiming(params.timing)

  return {
    motion: parseParticleMotion(params.motion),
    style,
    jitter,
    timing,
    random: parseParticleRandom(params.random),
  }
}

function parseParticleStyle(value: unknown): GSplatEffectTemplateParticleStyleParams {
  const style = value === undefined ? {} : requireRecord(value, 'params.style')
  assertNoUnknownKeys(style, PARTICLE_STYLE_KEYS, 'params.style')

  return {
    sizeMode: readEnum(
      style.sizeMode,
      GSPLAT_PARTICLE_SIZE_MODES,
      'params.style.sizeMode',
      GSPLAT_CHAOTIC_PARTICLE_SCATTER_DEFAULTS.particleSizeMode
    ),
    targetSize: readStrictNumber(
      style.targetSize,
      GSPLAT_PARTICLE_SCATTER_TEMPLATE_LIMITS.particleTargetSize,
      'params.style.targetSize',
      GSPLAT_CHAOTIC_PARTICLE_SCATTER_DEFAULTS.particleTargetSize
    ),
    targetSizeRatio: readStrictNumber(
      style.targetSizeRatio,
      GSPLAT_PARTICLE_SCATTER_TEMPLATE_LIMITS.particleTargetSizeRatio,
      'params.style.targetSizeRatio',
      GSPLAT_CHAOTIC_PARTICLE_SCATTER_DEFAULTS.particleTargetSizeRatio
    ),
    opacity: readStrictNumber(
      style.opacity,
      GSPLAT_PARTICLE_SCATTER_TEMPLATE_LIMITS.particleOpacity,
      'params.style.opacity',
      GSPLAT_CHAOTIC_PARTICLE_SCATTER_DEFAULTS.particleOpacity
    ),
    highlightStrength: readStrictNumber(
      style.highlightStrength,
      GSPLAT_PARTICLE_SCATTER_TEMPLATE_LIMITS.particleHighlightStrength,
      'params.style.highlightStrength',
      GSPLAT_CHAOTIC_PARTICLE_SCATTER_DEFAULTS.particleHighlightStrength
    ),
    glowStrength: readStrictNumber(
      style.glowStrength,
      GSPLAT_PARTICLE_SCATTER_TEMPLATE_LIMITS.particleGlowStrength,
      'params.style.glowStrength',
      GSPLAT_CHAOTIC_PARTICLE_SCATTER_DEFAULTS.particleGlowStrength
    ),
    glowRadius: readStrictNumber(
      style.glowRadius,
      GSPLAT_PARTICLE_SCATTER_TEMPLATE_LIMITS.particleGlowRadius,
      'params.style.glowRadius',
      GSPLAT_CHAOTIC_PARTICLE_SCATTER_DEFAULTS.particleGlowRadius
    ),
  }
}

function parseParticleJitter(value: unknown): GSplatEffectTemplateParticleJitterParams {
  const jitter = value === undefined ? {} : requireRecord(value, 'params.jitter')
  assertNoUnknownKeys(jitter, PARTICLE_JITTER_KEYS, 'params.jitter')

  return {
    amplitude: readStrictNumber(
      jitter.amplitude,
      GSPLAT_PARTICLE_SCATTER_TEMPLATE_LIMITS.particleJitterAmplitude,
      'params.jitter.amplitude',
      GSPLAT_CHAOTIC_PARTICLE_SCATTER_DEFAULTS.particleJitterAmplitude
    ),
    speed: readStrictNumber(
      jitter.speed,
      GSPLAT_PARTICLE_SCATTER_TEMPLATE_LIMITS.particleJitterSpeed,
      'params.jitter.speed',
      GSPLAT_CHAOTIC_PARTICLE_SCATTER_DEFAULTS.particleJitterSpeed
    ),
  }
}

function parseParticleTiming(value: unknown): GSplatEffectTemplateParticleTimingParams {
  const timing = value === undefined ? {} : requireRecord(value, 'params.timing')
  assertNoUnknownKeys(timing, PARTICLE_TIMING_KEYS, 'params.timing')

  const duration = readPositiveNumber(
    timing.duration,
    'params.timing.duration',
    GSPLAT_CHAOTIC_PARTICLE_SCATTER_DEFAULTS.duration
  )
  const peakHoldDuration = readNonNegativeNumber(
    timing.peakHoldDuration,
    'params.timing.peakHoldDuration',
    GSPLAT_CHAOTIC_PARTICLE_SCATTER_DEFAULTS.peakHoldDuration
  )
  const preScatterHoldDuration = readNonNegativeNumber(
    timing.preScatterHoldDuration,
    'params.timing.preScatterHoldDuration',
    GSPLAT_CHAOTIC_PARTICLE_SCATTER_DEFAULTS.preScatterHoldDuration
  )
  if (preScatterHoldDuration + peakHoldDuration >= duration) {
    throw new GSplatEffectTemplateError(
      'params.timing.preScatterHoldDuration + params.timing.peakHoldDuration must be less than params.timing.duration'
    )
  }

  return {
    duration,
    preScatterHoldDuration,
    peakHoldDuration,
    phaseWeights: parseParticlePhaseWeights(timing.phaseWeights),
  }
}

function parseParticleRandom(value: unknown): GSplatEffectTemplateParticleRandomParams {
  const random = value === undefined ? {} : requireRecord(value, 'params.random')
  assertNoUnknownKeys(random, PARTICLE_RANDOM_KEYS, 'params.random')

  return {
    seed: readStrictInteger(
      random.seed,
      GSPLAT_PARTICLE_SCATTER_TEMPLATE_LIMITS.seed,
      'params.random.seed',
      GSPLAT_CHAOTIC_PARTICLE_SCATTER_DEFAULTS.seed
    ),
  }
}

function parseParticleMotion(value: unknown): GSplatParticleMotion {
  if (value === undefined) {
    return {
      type: 'chaotic',
      params: { ...GSPLAT_PARTICLE_MOTION_DEFAULTS.chaotic },
    }
  }

  const motion = requireRecord(value, 'params.motion')
  assertNoUnknownKeys(motion, PARTICLE_MOTION_KEYS, 'params.motion')
  const type = readEnum(motion.type, GSPLAT_PARTICLE_MOTION_TYPES, 'params.motion.type', 'chaotic')
  const motionParams =
    motion.params === undefined ? {} : requireRecord(motion.params, 'params.motion.params')

  switch (type) {
    case 'chaotic': {
      assertNoUnknownKeys(motionParams, CHAOTIC_PARTICLE_MOTION_PARAM_KEYS, 'params.motion.params')
      return {
        type,
        params: {
          scatterScale: readStrictNumber(
            motionParams.scatterScale,
            GSPLAT_PARTICLE_MOTION_LIMITS.chaotic.scatterScale,
            'params.motion.params.scatterScale',
            GSPLAT_PARTICLE_MOTION_DEFAULTS.chaotic.scatterScale
          ),
        },
      }
    }
    case 'layered-print': {
      assertNoUnknownKeys(
        motionParams,
        LAYERED_PRINT_PARTICLE_MOTION_PARAM_KEYS,
        'params.motion.params'
      )
      return {
        type,
        params: {
          layerCount: readStrictInteger(
            motionParams.layerCount,
            GSPLAT_PARTICLE_MOTION_LIMITS['layered-print'].layerCount,
            'params.motion.params.layerCount',
            GSPLAT_PARTICLE_MOTION_DEFAULTS['layered-print'].layerCount
          ),
          bandWidth: readStrictNumber(
            motionParams.bandWidth,
            GSPLAT_PARTICLE_MOTION_LIMITS['layered-print'].bandWidth,
            'params.motion.params.bandWidth',
            GSPLAT_PARTICLE_MOTION_DEFAULTS['layered-print'].bandWidth
          ),
          liftScale: readStrictNumber(
            motionParams.liftScale,
            GSPLAT_PARTICLE_MOTION_LIMITS['layered-print'].liftScale,
            'params.motion.params.liftScale',
            GSPLAT_PARTICLE_MOTION_DEFAULTS['layered-print'].liftScale
          ),
          direction: readEnum(
            motionParams.direction,
            GSPLAT_LAYERED_PRINT_DIRECTIONS,
            'params.motion.params.direction',
            GSPLAT_PARTICLE_MOTION_DEFAULTS['layered-print'].direction
          ),
        },
      }
    }
    case 'fractal-orbit': {
      assertNoUnknownKeys(
        motionParams,
        FRACTAL_ORBIT_PARTICLE_MOTION_PARAM_KEYS,
        'params.motion.params'
      )
      return {
        type,
        params: {
          orbitScale: readStrictNumber(
            motionParams.orbitScale,
            GSPLAT_PARTICLE_MOTION_LIMITS['fractal-orbit'].orbitScale,
            'params.motion.params.orbitScale',
            GSPLAT_PARTICLE_MOTION_DEFAULTS['fractal-orbit'].orbitScale
          ),
          frequency: readStrictNumber(
            motionParams.frequency,
            GSPLAT_PARTICLE_MOTION_LIMITS['fractal-orbit'].frequency,
            'params.motion.params.frequency',
            GSPLAT_PARTICLE_MOTION_DEFAULTS['fractal-orbit'].frequency
          ),
          sweepWidth: readStrictNumber(
            motionParams.sweepWidth,
            GSPLAT_PARTICLE_MOTION_LIMITS['fractal-orbit'].sweepWidth,
            'params.motion.params.sweepWidth',
            GSPLAT_PARTICLE_MOTION_DEFAULTS['fractal-orbit'].sweepWidth
          ),
        },
      }
    }
  }
}

function parseParticlePhaseWeights(value: unknown): GSplatParticlePhaseWeights {
  const defaults = GSPLAT_CHAOTIC_PARTICLE_SCATTER_DEFAULTS.phaseWeights
  if (value === undefined) {
    return { ...defaults }
  }

  const weights = requireRecord(value, 'params.timing.phaseWeights')
  assertNoUnknownKeys(
    weights,
    GSPLAT_EFFECT_TEMPLATE_PARTICLE_PHASE_WEIGHT_KEYS,
    'params.timing.phaseWeights'
  )

  const phaseWeights = {
    particleize: readStrictNumber(
      weights.particleize,
      GSPLAT_PARTICLE_SCATTER_TEMPLATE_LIMITS.phaseWeight,
      'params.timing.phaseWeights.particleize',
      defaults.particleize
    ),
    scatter: readStrictNumber(
      weights.scatter,
      GSPLAT_PARTICLE_SCATTER_TEMPLATE_LIMITS.phaseWeight,
      'params.timing.phaseWeights.scatter',
      defaults.scatter
    ),
    gather: readStrictNumber(
      weights.gather,
      GSPLAT_PARTICLE_SCATTER_TEMPLATE_LIMITS.phaseWeight,
      'params.timing.phaseWeights.gather',
      defaults.gather
    ),
    restore: readStrictNumber(
      weights.restore,
      GSPLAT_PARTICLE_SCATTER_TEMPLATE_LIMITS.phaseWeight,
      'params.timing.phaseWeights.restore',
      defaults.restore
    ),
  }
  if (
    phaseWeights.particleize + phaseWeights.scatter + phaseWeights.gather + phaseWeights.restore <=
    0
  ) {
    throw new GSplatEffectTemplateError(
      'params.timing.phaseWeights must contain at least one positive value'
    )
  }

  return phaseWeights
}

function parseDissolveDriftParams(
  params: Record<string, unknown>
): GSplatEffectTemplateDissolveDriftParams {
  assertNoUnknownKeys(params, DISSOLVE_DRIFT_PARAM_KEYS, 'params')

  return {
    speed: readNumber(params.speed, NUMERIC_LIMITS.dissolveSpeed, 'params.speed', 1),
    motionScale: readNumber(
      params.motionScale,
      NUMERIC_LIMITS.motionScale,
      'params.motionScale',
      1
    ),
  }
}

function normalizeRuntimeContext(
  context: GSplatEffectTemplateRuntimeContext
): NormalizedRuntimeContext {
  const record = requireRecord(context, 'runtime context must be an object')
  if (record.effectSpace !== 'effect-local') {
    throw new GSplatEffectTemplateError('runtime context effectSpace must be "effect-local"')
  }

  return {
    center: readVector3(record.center, 'runtime.center'),
    effectBounds: readOptionalBox3(record.effectBounds, 'runtime.effectBounds'),
    sceneBounds: readOptionalBox3(record.sceneBounds, 'runtime.sceneBounds'),
    sceneRadius: readOptionalPositiveNumber(record.sceneRadius, 'runtime.sceneRadius'),
    defaultSceneRadius: readPositiveNumber(
      record.defaultSceneRadius,
      'runtime.defaultSceneRadius',
      DEFAULT_SCENE_RADIUS
    ),
    minRevealRadius: readPositiveNumber(
      record.minRevealRadius,
      'runtime.minRevealRadius',
      DEFAULT_MIN_REVEAL_RADIUS
    ),
    camera:
      record.camera === undefined || record.camera === null
        ? null
        : readCameraContext(record.camera),
  }
}

function createDiagnostics(
  effectKey: GSplatEffectTemplateKey,
  runtime: NormalizedRuntimeContext
): GSplatEffectTemplateDiagnostics {
  const radiusInfo = resolveSceneRadius(runtime)

  return {
    effectKey,
    sceneRadius: radiusInfo.radius,
    usedEffectBounds: radiusInfo.usedEffectBounds,
    usedSceneBounds: radiusInfo.usedSceneBounds,
  }
}

function createBoxScanEffect(
  template: GSplatEffectTemplateBoxScan,
  runtime: NormalizedRuntimeContext
): GSplatShaderEffectLike {
  const params = template.params
  const targetBox = createTargetBox(runtime.center, params.size)
  const sweepBox = createSweepBox(targetBox, runtime.effectBounds ?? runtime.sceneBounds)
  const direction = createBoxDirection(params.direction, shouldReverseBoxScan(template.effectKey))
  const tintColor =
    template.effectKey === 'box-tint' || template.effectKey === 'box-untint'
      ? createColor(params.tintColor)
      : new THREE.Color(1, 1, 1)

  return new GSplatBoxShaderEffect({
    enabled: false,
    aabbMin: toTuple(targetBox.min),
    aabbMax: toTuple(targetBox.max),
    sweepMin: toTuple(sweepBox.min),
    sweepMax: toTuple(sweepBox.max),
    direction: toTuple(direction),
    duration: params.duration,
    visibleStart: template.effectKey !== 'box-reveal',
    visibleEnd: template.effectKey !== 'box-hide',
    interval: template.effectKey === 'box-reveal' || template.effectKey === 'box-hide' ? 0.1 : 0.05,
    invertTint: template.effectKey === 'box-untint',
    baseTint: new THREE.Color(1, 1, 1),
    edgeTint: createColor(params.edgeColor, BOX_EDGE_TINT_INTENSITY),
    tint: tintColor,
  })
}

function createBoxCropEffect(
  template: GSplatEffectTemplateBoxCrop,
  runtime: NormalizedRuntimeContext
): GSplatShaderEffectLike {
  const targetBox = createTargetBox(runtime.center, template.params.size)

  return new GSplatCropShaderEffect({
    enabled: false,
    aabbMin: toTuple(targetBox.min),
    aabbMax: toTuple(targetBox.max),
    edgeScaleFactor: template.params.edgeScaleFactor,
  })
}

function createRadialEffect(
  template: GSplatEffectTemplateRevealWave,
  runtime: NormalizedRuntimeContext,
  sceneRadius: number
): GSplatShaderEffectLike {
  const scale = createRevealAdaptiveScale(runtime, sceneRadius)
  if (!template.adaptive) {
    const params = template.params as GSplatEffectTemplateRevealWaveManualParams
    return new GSplatRevealRadial({
      enabled: false,
      center: toTuple(runtime.center),
      speed: params.speed,
      acceleration: params.acceleration,
      delay: params.delay,
      endRadius: scale.revealRadius,
      dotTint: createColorControlColor(params.dotTint),
      waveTint: createColorControlColor(params.waveTint),
      oscillationIntensity: params.oscillationIntensity,
      bandWidth: params.bandWidth,
    })
  }

  const params = template.params as GSplatEffectTemplateRevealWaveParams
  const intensity = Math.max(0, params.intensity)
  const timing = createDoubleWaveTiming(params.duration, scale.revealRadius)

  return new GSplatRevealRadial({
    enabled: false,
    center: toTuple(runtime.center),
    speed: timing.speed,
    acceleration: 0,
    delay: timing.delay,
    endRadius: scale.revealRadius,
    dotTint: createColorControlColor(params.dotTint),
    waveTint: createColorControlColor(params.waveTint),
    oscillationIntensity: scale.detailScale * 0.18 * intensity,
    bandWidth: clampNumber(scale.detailScale * 0.22, {
      min: scale.revealRadius * 0.025,
      max: getRadialBandMax(scale.revealRadius),
    }),
    dotSize: scale.detailScale * 0.035 * Math.max(0.35, intensity),
    maxRoundSize: scale.detailScale * 0.06 * Math.max(0.5, intensity),
    revealBlendDistance: clampNumber(scale.detailScale * 0.18, {
      min: scale.revealRadius * 0.02,
      max: getRadialBlendMax(scale.revealRadius),
    }),
  })
}

function createRainEffect(
  template: GSplatEffectTemplateRain,
  runtime: NormalizedRuntimeContext,
  sceneRadius: number
): GSplatShaderEffectLike {
  const scale = createRevealAdaptiveScale(runtime, sceneRadius)
  if (!template.adaptive) {
    const params = template.params as GSplatEffectTemplateRainManualParams
    return new GSplatRevealRain({
      enabled: false,
      center: toTuple(runtime.center),
      distance: params.distance,
      speed: params.speed,
      acceleration: params.acceleration,
      flightTime: params.flightTime,
      rainSize: params.rainSize,
      rotation: params.rotation,
      fallTint: createColorControlColor(params.fallTint),
      fallTintIntensity: params.fallTintIntensity,
      hitTint: createColorControlColor(params.hitTint),
      hitDuration: params.hitDuration,
      endRadius: scale.revealRadius,
    })
  }

  const params = template.params as GSplatEffectTemplateRainParams
  const intensity = Math.max(0, params.intensity)
  const distance = getRainDistance(
    scale.revealRadius,
    scale.viewRadius,
    scale.detailScale,
    intensity
  )
  const timing = createRainTiming(params.duration, scale.revealRadius, scale.viewRadius)

  return new GSplatRevealRain({
    enabled: false,
    center: toTuple(runtime.center),
    distance,
    speed: timing.speed,
    acceleration: timing.acceleration,
    spreadTime: timing.spreadTime,
    spreadCurve: timing.spreadCurve,
    waveRadius: timing.waveRadius,
    flightTime: timing.flightTime,
    dropJitter: timing.dropJitter,
    rainSize: getRainSize(scale.revealRadius, scale.viewRadius, scale.detailScale, intensity),
    maxRoundSize: getRainMaxRoundSize(
      scale.revealRadius,
      scale.viewRadius,
      scale.detailScale,
      intensity
    ),
    fallVisibleRatio: 0.45,
    rotation: 0,
    fallTint: createColorControlColor(params.fallTint),
    fallTintIntensity: params.fallTintIntensity,
    hitTint: createColorControlColor(params.hitTint),
    hitDuration: timing.hitDuration,
    endRadius: scale.revealRadius,
    startRadius: scale.revealRadius * 0.15,
  })
}

function createGridEffect(
  template: GSplatEffectTemplateGrid,
  runtime: NormalizedRuntimeContext,
  sceneRadius: number
): GSplatShaderEffectLike {
  const scale = createRevealAdaptiveScale(runtime, sceneRadius)
  if (!template.adaptive) {
    const params = template.params as GSplatEffectTemplateGridManualParams
    return new GSplatRevealGridEruption({
      enabled: false,
      center: toTuple(runtime.center),
      blockSize: params.blockSize,
      delay: params.delay,
      duration: params.duration,
      dotSize: params.dotSize,
      moveTint: createColorControlColor(params.moveTint),
      moveTintIntensity: params.moveTintIntensity,
      landTint: createColorControlColor(params.landTint),
      landDuration: params.landDuration,
      endRadius: scale.revealRadius,
    })
  }

  const params = template.params as GSplatEffectTemplateGridParams
  const intensity = Math.max(0, params.intensity)
  const timing = createGridTiming(params.duration, scale.revealRadius)

  return new GSplatRevealGridEruption({
    enabled: false,
    center: toTuple(runtime.center),
    blockSize: getGridBlockSize(scale.revealRadius),
    delay: timing.delay,
    duration: timing.blockMoveDuration,
    dotSize: getGridDotSize(scale.revealRadius, scale.viewRadius, intensity),
    moveTint: createColorControlColor(params.moveTint),
    moveTintIntensity: params.moveTintIntensity,
    landTint: createColorControlColor(params.landTint),
    landDuration: timing.landDuration,
    endRadius: scale.revealRadius,
  })
}

function createObbDoubleWaveEffect(
  template: GSplatEffectTemplateRevealWave,
  runtime: NormalizedRuntimeContext
): GSplatShaderEffectLike {
  const bounds = runtime.effectBounds ?? runtime.sceneBounds
  if (!bounds || bounds.isEmpty()) {
    throw new GSplatEffectTemplateError('obb-double-wave requires non-empty effect-local bounds')
  }
  if (!runtime.camera) {
    throw new GSplatEffectTemplateError('obb-double-wave requires effect-local camera context')
  }

  const adaptiveObb = createCameraAdaptiveObb({
    cameraPosition: runtime.camera.position,
    cameraTarget: runtime.camera.target,
    up: runtime.camera.up,
    bounds,
  })
  const sweepLength = Math.max(EPSILON, adaptiveObb.sweepEnd - adaptiveObb.sweepStart)
  const scale = createRevealAdaptiveScale(runtime, sweepLength)
  if (!template.adaptive) {
    const params = template.params as GSplatEffectTemplateRevealWaveManualParams
    const effect = new GSplatObbShaderEffect({
      enabled: false,
      mode: 'double-wave-reveal',
      origin: adaptiveObb.origin,
      axisX: adaptiveObb.axisX,
      axisY: adaptiveObb.axisY,
      axisZ: adaptiveObb.axisZ,
      min: adaptiveObb.min,
      max: adaptiveObb.max,
      direction: adaptiveObb.direction,
      sweepStart: adaptiveObb.sweepStart,
      sweepEnd: adaptiveObb.sweepEnd,
      speed: params.speed,
      acceleration: params.acceleration,
      delay: params.delay,
      dotTint: createColorControlColor(params.dotTint),
      waveTint: createColorControlColor(params.waveTint),
      oscillationIntensity: params.oscillationIntensity,
      bandWidth: params.bandWidth,
    })
    ;(effect as GSplatShaderEffectLike & { diagnostics?: Record<string, unknown> }).diagnostics = {
      effectKey: template.effectKey,
      bounds: {
        min: toTuple(bounds.min),
        max: toTuple(bounds.max),
        size: toTuple(new THREE.Vector3().subVectors(bounds.max, bounds.min)),
      },
      runtime: {
        center: toTuple(runtime.center),
        sceneRadius: runtime.sceneRadius,
        defaultSceneRadius: runtime.defaultSceneRadius,
        minRevealRadius: runtime.minRevealRadius,
      },
      camera: {
        position: toTuple(runtime.camera.position),
        target: toTuple(runtime.camera.target),
        up: toTuple(runtime.camera.up),
      },
      adaptiveObb: {
        origin: adaptiveObb.origin,
        axisX: adaptiveObb.axisX,
        axisY: adaptiveObb.axisY,
        axisZ: adaptiveObb.axisZ,
        min: adaptiveObb.min,
        max: adaptiveObb.max,
        direction: adaptiveObb.direction,
        sweepStart: adaptiveObb.sweepStart,
        sweepEnd: adaptiveObb.sweepEnd,
        sweepLength,
      },
      adaptiveScale: {
        revealRadius: scale.revealRadius,
        viewRadius: scale.viewRadius,
        detailScale: scale.detailScale,
      },
      manual: {
        speed: params.speed,
        acceleration: params.acceleration,
        delay: params.delay,
        bandWidth: params.bandWidth,
        oscillationIntensity: params.oscillationIntensity,
      },
    }
    return effect
  }

  const params = template.params as GSplatEffectTemplateRevealWaveParams
  const intensity = Math.max(0, params.intensity)
  const timing = createDoubleWaveTiming(params.duration, sweepLength)
  const bandWidth = clampNumber(scale.detailScale * 0.22, {
    min: scale.revealRadius * 0.025,
    max: getRadialBandMax(scale.revealRadius),
  })
  const dotSize = scale.detailScale * 0.035 * Math.max(0.35, intensity)
  const maxRoundSize = scale.detailScale * 0.06 * Math.max(0.5, intensity)
  const revealBlendDistance = clampNumber(scale.detailScale * 0.18, {
    min: scale.revealRadius * 0.02,
    max: getRadialBlendMax(scale.revealRadius),
  })

  const effect = new GSplatObbShaderEffect({
    enabled: false,
    mode: 'double-wave-reveal',
    origin: adaptiveObb.origin,
    axisX: adaptiveObb.axisX,
    axisY: adaptiveObb.axisY,
    axisZ: adaptiveObb.axisZ,
    min: adaptiveObb.min,
    max: adaptiveObb.max,
    direction: adaptiveObb.direction,
    sweepStart: adaptiveObb.sweepStart,
    sweepEnd: adaptiveObb.sweepEnd,
    speed: timing.speed,
    acceleration: 0,
    delay: timing.delay,
    dotTint: createColorControlColor(params.dotTint),
    waveTint: createColorControlColor(params.waveTint),
    oscillationIntensity: scale.detailScale * 0.18 * intensity,
    bandWidth,
    dotSize,
    maxRoundSize,
    revealBlendDistance,
  })
  ;(effect as GSplatShaderEffectLike & { diagnostics?: Record<string, unknown> }).diagnostics = {
    effectKey: template.effectKey,
    bounds: {
      min: toTuple(bounds.min),
      max: toTuple(bounds.max),
      size: toTuple(new THREE.Vector3().subVectors(bounds.max, bounds.min)),
    },
    runtime: {
      center: toTuple(runtime.center),
      sceneRadius: runtime.sceneRadius,
      defaultSceneRadius: runtime.defaultSceneRadius,
      minRevealRadius: runtime.minRevealRadius,
    },
    camera: {
      position: toTuple(runtime.camera.position),
      target: toTuple(runtime.camera.target),
      up: toTuple(runtime.camera.up),
    },
    adaptiveObb: {
      origin: adaptiveObb.origin,
      axisX: adaptiveObb.axisX,
      axisY: adaptiveObb.axisY,
      axisZ: adaptiveObb.axisZ,
      min: adaptiveObb.min,
      max: adaptiveObb.max,
      direction: adaptiveObb.direction,
      sweepStart: adaptiveObb.sweepStart,
      sweepEnd: adaptiveObb.sweepEnd,
      sweepLength,
    },
    adaptiveScale: {
      revealRadius: scale.revealRadius,
      viewRadius: scale.viewRadius,
      detailScale: scale.detailScale,
    },
    derived: {
      duration: params.duration,
      speed: timing.speed,
      delay: timing.delay,
      bandWidth,
      dotSize,
      maxRoundSize,
      revealBlendDistance,
      oscillationIntensity: scale.detailScale * 0.18 * intensity,
    },
  }
  return effect
}

function createVisualShowcaseEffect(
  template: GSplatEffectTemplateVisualShowcase,
  runtime: NormalizedRuntimeContext
): GSplatShaderEffectLike {
  const params = template.params
  const options = {
    enabled: false,
    center: toTuple(runtime.center),
    intensity: params.intensity,
    timeScale: params.timeScale,
    effectScale: createShowcaseEffectScale(runtime),
  }

  switch (template.effectKey) {
    case 'visual-electronic':
      return new GSplatVisualElectronicEffect({
        ...options,
        swayAmplitude: params.swayAmplitude,
        swaySpeed: params.swaySpeed,
        flickerColor: createColorControlColor(
          params.flickerColor ?? { color: '#0080b3', intensity: 1 }
        ),
      })
    case 'visual-deep-meditation':
      return new GSplatVisualDeepMeditationEffect(options)
    case 'visual-waves':
      return new GSplatVisualWavesEffect(options)
    case 'visual-flare':
      return new GSplatVisualFlareEffect(options)
    case 'visual-disintegrate':
      return new GSplatVisualDisintegrateEffect(options)
  }
}

function createRevealShowcaseEffect(
  template: GSplatEffectTemplateRevealShowcase,
  runtime: NormalizedRuntimeContext,
  sceneRadius: number
): GSplatShaderEffectLike {
  const params = template.params
  const options = {
    enabled: false,
    center: toTuple(runtime.center),
    timeScale: params.timeScale,
    motionScale: params.motionScale,
    duration: params.duration,
  }

  switch (template.effectKey) {
    case 'reveal-magic':
      return new GSplatRevealMagicEffect({
        ...options,
        revealRadius: sceneRadius,
        effectScale: createMagicEffectScale(runtime),
      })
    case 'reveal-spread':
      return new GSplatRevealSpreadEffect(options)
    case 'reveal-unroll':
      return new GSplatRevealUnrollEffect({
        ...options,
        heightRange: createUnrollHeightRange(
          runtime.effectBounds ?? runtime.sceneBounds,
          runtime.center
        ),
        effectScale: createBoundsEffectScale(runtime, UNROLL_REFERENCE_RADIUS),
      })
    case 'reveal-twister':
      return new GSplatRevealTwisterEffect({
        ...options,
        ...createTwisterBounds(
          runtime.effectBounds ?? runtime.sceneBounds,
          runtime.center,
          sceneRadius,
          runtime.camera
        ),
      })
  }
}

function createUnrollHeightRange(
  bounds: THREE.Box3 | null,
  center: THREE.Vector3
): readonly [number, number] | undefined {
  if (!bounds || bounds.isEmpty()) {
    return undefined
  }

  return [center.z - bounds.max.z, center.z - bounds.min.z]
}

function createTwisterBounds(
  bounds: THREE.Box3 | null,
  center: THREE.Vector3,
  sceneRadius: number,
  camera: NormalizedRuntimeContext['camera']
): {
  funnelHeight?: number
  horizontalRadius?: number
  revealRadius?: number
  heightRange?: readonly [number, number]
} {
  if (!bounds || bounds.isEmpty()) {
    return {}
  }

  const boundsRadius = Math.max(
    Math.abs(bounds.min.x - center.x),
    Math.abs(bounds.max.x - center.x),
    Math.abs(bounds.min.y - center.y),
    Math.abs(bounds.max.y - center.y)
  )
  const cameraRadius = camera ? camera.position.distanceTo(camera.target) : sceneRadius

  const horizontalRadius = Math.max(1, Math.min(boundsRadius, sceneRadius, cameraRadius))

  return {
    funnelHeight: Math.max(1, Math.min(bounds.max.z - bounds.min.z, horizontalRadius * 2)),
    horizontalRadius,
    revealRadius: Math.max(1, boundsRadius),
    heightRange: [bounds.min.z - center.z, bounds.max.z - center.z],
  }
}

function createShowcaseEffectScale(runtime: NormalizedRuntimeContext): number {
  const radius = resolveSceneRadius(runtime).radius
  const cameraRadius = runtime.camera
    ? runtime.camera.position.distanceTo(runtime.camera.target)
    : radius
  return Math.max(1, Math.min(radius, cameraRadius))
}

function createMagicEffectScale(runtime: NormalizedRuntimeContext): number {
  return createBoundsEffectScale(runtime, MAGIC_REFERENCE_RADIUS)
}

function createBoundsEffectScale(
  runtime: NormalizedRuntimeContext,
  referenceRadius: number
): number {
  const bounds = runtime.effectBounds ?? runtime.sceneBounds
  if (!bounds || bounds.isEmpty()) {
    return 1
  }

  const radius = bounds.getBoundingSphere(new THREE.Sphere()).radius
  return clampNumber(radius / referenceRadius, {
    min: EPSILON,
    max: 1,
  })
}

function createRevealAdaptiveScale(
  runtime: NormalizedRuntimeContext,
  sceneRadius: number
): RevealAdaptiveScale {
  const modelRadius = Math.max(EPSILON, sceneRadius)
  const cameraRadius = runtime.camera
    ? runtime.camera.position.distanceTo(runtime.camera.target)
    : modelRadius
  const viewRadius = Math.max(EPSILON, Math.min(modelRadius, Math.max(cameraRadius, EPSILON)))
  const detailScale = Math.max(EPSILON, Math.min(modelRadius, viewRadius))

  return {
    modelRadius,
    viewRadius,
    revealRadius: modelRadius,
    detailScale,
  }
}

function createDoubleWaveTiming(duration: number, sweepDistance: number): DoubleWaveTiming {
  const delay = Math.min(duration * DOUBLE_WAVE_DELAY_RATIO, DOUBLE_WAVE_MAX_DELAY)
  const sweepTime = Math.max(EPSILON, duration - delay)

  return {
    speed: Math.max(EPSILON, sweepDistance) / sweepTime,
    delay,
  }
}

function createRainTiming(duration: number, revealRadius: number, viewRadius: number): RainTiming {
  const hitDuration = clampNumber(duration * RAIN_TAIL_RATIO, {
    min: 0.5,
    max: 0.9,
  })
  const flightTime = clampNumber(duration * RAIN_FLIGHT_RATIO, {
    min: 0.35,
    max: duration * 0.45,
  })
  const dropJitter = clampNumber(duration * RAIN_JITTER_RATIO, {
    min: 0,
    max: flightTime * 0.8,
  })
  const spreadTime = Math.max(
    EPSILON,
    duration - flightTime * RAIN_MAX_FLIGHT_SCALE - dropJitter - hitDuration
  )
  const wave = createRainWaveTiming(revealRadius, viewRadius, spreadTime)

  return {
    speed: wave.speed,
    acceleration: wave.acceleration,
    spreadTime,
    spreadCurve: wave.spreadCurve,
    waveRadius: Math.max(EPSILON, revealRadius),
    flightTime,
    dropJitter,
    hitDuration,
  }
}

function createRainWaveTiming(
  revealRadius: number,
  viewRadius: number,
  spreadTime: number
): {
  speed: number
  acceleration: number
  spreadCurve: number
} {
  const radius = Math.max(EPSILON, revealRadius)
  const localRadius = clampNumber(viewRadius, {
    min: EPSILON,
    max: radius,
  })
  const viewRatio = localRadius / radius
  const localTimeRatio = viewRatio + (1 - viewRatio) * 0.45

  if (localTimeRatio >= 0.98) {
    return {
      speed: radius / spreadTime,
      acceleration: 0,
      spreadCurve: 1,
    }
  }

  return {
    speed: radius / spreadTime,
    acceleration: 0,
    spreadCurve: clampNumber(Math.log(localTimeRatio) / Math.log(viewRatio), {
      min: 0.28,
      max: 1,
    }),
  }
}

function createGridTiming(duration: number, revealRadius: number): GridTiming {
  const landDuration = clampNumber(duration * GRID_LAND_TAIL_RATIO, {
    min: 0.3,
    max: 0.7,
  })
  const blockMoveDuration = clampNumber(duration * GRID_MOVE_RATIO, {
    min: 0.25,
    max: duration * 0.6,
  })
  const spreadTime = Math.max(EPSILON, duration - blockMoveDuration - landDuration)

  return {
    delay: spreadTime / Math.max(EPSILON, revealRadius),
    blockMoveDuration,
    landDuration,
  }
}

function getRadialBandMax(revealRadius: number): number {
  return Math.min(revealRadius * 0.18, Math.sqrt(revealRadius) * 0.22)
}

function getRadialBlendMax(revealRadius: number): number {
  return Math.min(revealRadius * 0.12, Math.sqrt(revealRadius) * 0.16)
}

function getRainDistance(
  revealRadius: number,
  viewRadius: number,
  detailScale: number,
  intensity: number
): number {
  const styleScale = Math.max(0.45, intensity)
  const viewRatio = clampNumber(viewRadius / Math.max(EPSILON, revealRadius), {
    min: 0,
    max: 1,
  })
  const overviewWeight = smoothstep(0.35, 0.85, viewRatio)
  const localMin = detailScale * 0.9
  const overviewMin = revealRadius * 0.9
  const minDistance = mix(localMin, overviewMin, overviewWeight)
  const localMax = Math.max(localMin, Math.sqrt(detailScale) * 2.7)
  const overviewMax = Math.max(overviewMin, Math.sqrt(revealRadius) * 2.7)
  const maxDistance = mix(localMax, overviewMax, overviewWeight)

  return clampNumber(detailScale * 3.6 * styleScale, {
    min: minDistance,
    max: maxDistance,
  })
}

function getRainSize(
  revealRadius: number,
  viewRadius: number,
  detailScale: number,
  intensity: number
): number {
  const styleScale = Math.max(0.35, intensity)
  const baseSize = detailScale * 0.035 * styleScale
  const viewRatio = clampNumber(viewRadius / Math.max(EPSILON, revealRadius), {
    min: 0,
    max: 1,
  })
  const overviewWeight = smoothstep(0.35, 0.85, viewRatio)
  const localMax = Math.max(detailScale * 0.012, Math.sqrt(detailScale) * 0.045)
  const overviewMax = detailScale * 0.035 * styleScale

  return Math.min(baseSize, mix(localMax, overviewMax, overviewWeight))
}

function getRainMaxRoundSize(
  revealRadius: number,
  viewRadius: number,
  detailScale: number,
  intensity: number
): number {
  const styleScale = Math.max(0.5, intensity)
  const baseSize = detailScale * 0.06 * styleScale
  const viewRatio = clampNumber(viewRadius / Math.max(EPSILON, revealRadius), {
    min: 0,
    max: 1,
  })
  const overviewWeight = smoothstep(0.35, 0.85, viewRatio)
  const localMax = Math.max(detailScale * 0.018, Math.sqrt(detailScale) * 0.065)
  const overviewMax = detailScale * 0.06 * styleScale

  return Math.min(baseSize, mix(localMax, overviewMax, overviewWeight))
}

function smoothstep(edge0: number, edge1: number, value: number): number {
  const t = clampNumber((value - edge0) / Math.max(EPSILON, edge1 - edge0), {
    min: 0,
    max: 1,
  })
  return t * t * (3 - 2 * t)
}

function mix(a: number, b: number, t: number): number {
  return a * (1 - t) + b * t
}

function getGridScale(revealRadius: number): number {
  return clampNumber(revealRadius / PLAYCANVAS_REFERENCE_RADIUS, {
    min: EPSILON,
    max: 1,
  })
}

function getGridBlockSize(revealRadius: number): number {
  return GRID_REFERENCE_BLOCK_SIZE * getGridScale(revealRadius)
}

function getGridDotSize(revealRadius: number, viewRadius: number, intensity: number): number {
  const styleScale = Math.max(0.35, intensity)
  const referenceSize = GRID_REFERENCE_DOT_SIZE * getGridScale(revealRadius) * styleScale
  const distanceSize = Math.sqrt(Math.max(EPSILON, viewRadius)) * 0.006 * styleScale
  const maxSize = Math.max(referenceSize, Math.sqrt(Math.max(EPSILON, revealRadius)) * 0.018)

  return Math.min(Math.max(referenceSize, distanceSize), maxSize)
}

function createParticleScatterEffect(
  template: GSplatEffectTemplateParticleScatter,
  runtime: NormalizedRuntimeContext,
  sceneRadius: number
): GSplatShaderEffectLike {
  const params = template.params
  const effectScale = Math.max(EPSILON, sceneRadius)
  const cameraRadius = runtime.camera
    ? runtime.camera.position.distanceTo(runtime.camera.target)
    : effectScale
  const particleSizeReferenceScale = Math.max(
    EPSILON,
    Math.min(effectScale, Math.max(EPSILON, cameraRadius))
  )
  const commonOptions = {
    enabled: false,
    duration: params.timing.duration,
    preScatterHoldDuration: params.timing.preScatterHoldDuration,
    peakHoldDuration: params.timing.peakHoldDuration,
    phaseWeights: params.timing.phaseWeights,
    effectScale,
    particleSizeMode: params.style.sizeMode,
    particleTargetSize: params.style.targetSize,
    particleTargetSizeRatio: params.style.targetSizeRatio,
    particleSizeReferenceScale,
    particleOpacity: params.style.opacity,
    particleHighlightStrength: params.style.highlightStrength,
    particleGlowStrength: params.style.glowStrength,
    particleGlowRadius: params.style.glowRadius,
    particleJitterAmplitude: params.jitter.amplitude,
    particleJitterSpeed: params.jitter.speed,
    seed: params.random.seed,
  }
  const bounds = createParticleMotionBounds(runtime, effectScale)
  const effect = createParticleMotionEffect(params.motion, commonOptions, bounds)
  ;(effect as GSplatShaderEffectLike & { diagnostics?: Record<string, unknown> }).diagnostics = {
    effectKey: template.effectKey,
    runtime: {
      center: toTuple(runtime.center),
      modelRadius: effectScale,
    },
    derived: {
      particleSizeMode: params.style.sizeMode,
      particleTargetSize: params.style.targetSize,
      particleTargetSizeRatio: params.style.targetSizeRatio,
      particleSizeReferenceScale,
      particleOpacity: params.style.opacity,
      particleHighlightStrength: params.style.highlightStrength,
      particleGlowStrength: params.style.glowStrength,
      particleGlowRadius: params.style.glowRadius,
      particleJitterAmplitude: params.jitter.amplitude,
      particleJitterSpeed: params.jitter.speed,
      motion: params.motion,
      effectScale,
    },
  }
  return effect
}

function createParticleMotionEffect(
  motion: GSplatParticleMotion,
  commonOptions: ConstructorParameters<typeof GSplatChaoticParticleScatterEffect>[0],
  bounds: THREE.Box3
): GSplatParticleLifecycleEffect {
  switch (motion.type) {
    case 'chaotic':
      return new GSplatChaoticParticleScatterEffect({
        ...commonOptions,
        scatterScale: motion.params.scatterScale,
      })
    case 'layered-print':
      return new GSplatLayeredPrintParticleEffect({
        ...commonOptions,
        boundsMin: toTuple(bounds.min),
        boundsMax: toTuple(bounds.max),
        ...motion.params,
      })
    case 'fractal-orbit':
      return new GSplatFractalOrbitParticleEffect({
        ...commonOptions,
        boundsMin: toTuple(bounds.min),
        boundsMax: toTuple(bounds.max),
        ...motion.params,
      })
  }
}

function createParticleMotionBounds(
  runtime: NormalizedRuntimeContext,
  effectScale: number
): THREE.Box3 {
  const source = runtime.effectBounds ?? runtime.sceneBounds
  if (source && !source.isEmpty()) {
    return source.clone()
  }

  const halfSize = new THREE.Vector3(effectScale, effectScale, effectScale)
  return new THREE.Box3(runtime.center.clone().sub(halfSize), runtime.center.clone().add(halfSize))
}

function createDissolveDriftEffect(
  template: GSplatEffectTemplateDissolveDrift,
  runtime: NormalizedRuntimeContext
): GSplatShaderEffectLike {
  const params = template.params

  return new GSplatDissolveDriftEffect({
    enabled: false,
    speed: params.speed,
    motionScale: params.motionScale,
    effectScale: createShowcaseEffectScale(runtime),
  })
}

function requireRecord(value: unknown, path: string): Record<string, unknown> {
  if (!value || typeof value !== 'object' || Array.isArray(value)) {
    throw new GSplatEffectTemplateError(`${path} must be an object`)
  }
  return value as Record<string, unknown>
}

function assertNoUnknownKeys(
  record: Record<string, unknown>,
  allowedKeys: readonly string[],
  path: string
): void {
  const allowed = new Set(allowedKeys)
  const unknownKeys = Object.keys(record).filter((key) => !allowed.has(key))
  if (unknownKeys.length > 0) {
    throw new GSplatEffectTemplateError(
      `${path} contains unsupported fields: ${unknownKeys.join(', ')}`
    )
  }
}

function readString(value: unknown, path: string): string {
  if (typeof value !== 'string') {
    throw new GSplatEffectTemplateError(`${path} must be a string`)
  }
  return value
}

function readNonEmptyString(value: unknown, path: string): string {
  const text = readString(value, path).trim()
  if (!text) {
    throw new GSplatEffectTemplateError(`${path} must not be empty`)
  }
  return text
}

function readEffectKey(value: unknown): GSplatEffectTemplateKey {
  if (typeof value === 'string' && EFFECT_KEY_SET.has(value)) {
    return value as GSplatEffectTemplateKey
  }
  throw new GSplatEffectTemplateError(
    `Unsupported GSplat effect template effectKey: ${String(value)}`
  )
}

function readNumber(value: unknown, limit: NumericLimit, path: string, fallback: number): number {
  if (value === undefined) {
    return fallback
  }
  if (typeof value !== 'number' || !Number.isFinite(value)) {
    throw new GSplatEffectTemplateError(`${path} must be a finite number`)
  }
  return clampNumber(value, limit)
}

function readStrictNumber(
  value: unknown,
  limit: NumericLimit,
  path: string,
  fallback: number
): number {
  if (value === undefined) {
    return fallback
  }
  if (typeof value !== 'number' || !Number.isFinite(value)) {
    throw new GSplatEffectTemplateError(`${path} must be a finite number`)
  }
  if (value < limit.min || value > limit.max) {
    throw new GSplatEffectTemplateError(`${path} must be between ${limit.min} and ${limit.max}`)
  }
  return value
}

function readStrictInteger(
  value: unknown,
  limit: NumericLimit,
  path: string,
  fallback: number
): number {
  const parsed = readStrictNumber(value, limit, path, fallback)
  if (!Number.isInteger(parsed)) {
    throw new GSplatEffectTemplateError(`${path} must be an integer`)
  }
  return parsed
}

function readNonNegativeNumber(value: unknown, path: string, fallback: number): number {
  if (value === undefined) {
    return fallback
  }
  if (typeof value !== 'number' || !Number.isFinite(value) || value < 0) {
    throw new GSplatEffectTemplateError(`${path} must be a non-negative finite number`)
  }
  return value
}

function readEnum<T extends readonly string[]>(
  value: unknown,
  allowedValues: T,
  path: string,
  fallback: T[number]
): T[number] {
  if (value === undefined) {
    return fallback
  }
  if (typeof value === 'string' && allowedValues.includes(value)) {
    return value
  }
  throw new GSplatEffectTemplateError(`${path} is not supported`)
}

function readHexColor(value: unknown, path: string, fallback: string): string {
  if (value === undefined) {
    return fallback
  }
  if (typeof value !== 'string') {
    throw new GSplatEffectTemplateError(`${path} must be a hex color string`)
  }
  return normalizeHexColor(value, path)
}

function readColorControl(
  value: unknown,
  path: string,
  fallback: GSplatEffectTemplateColorControl
): GSplatEffectTemplateColorControl {
  if (value === undefined) {
    return { ...fallback }
  }

  const record = requireRecord(value, path)
  assertNoUnknownKeys(record, COLOR_CONTROL_KEYS, path)

  return {
    color: readHexColor(record.color, `${path}.color`, fallback.color),
    intensity: readNumber(
      record.intensity,
      NUMERIC_LIMITS.colorIntensity,
      `${path}.intensity`,
      fallback.intensity
    ),
  }
}

function normalizeHexColor(value: string, path: string): string {
  const hex = /^#?([0-9a-f]{6})$/i.exec(value.trim())?.[1]
  if (!hex) {
    throw new GSplatEffectTemplateError(`${path} must be a 6-digit hex color`)
  }
  return `#${hex.toLowerCase()}`
}

function clampNumber(value: number, limit: NumericLimit): number {
  return Math.min(limit.max, Math.max(limit.min, value))
}

function readVector3(value: unknown, path: string): THREE.Vector3 {
  if (Array.isArray(value)) {
    if (value.length !== 3) {
      throw new GSplatEffectTemplateError(`${path} must contain exactly 3 numbers`)
    }
    return new THREE.Vector3(
      readFiniteNumber(value[0], `${path}[0]`),
      readFiniteNumber(value[1], `${path}[1]`),
      readFiniteNumber(value[2], `${path}[2]`)
    )
  }

  const record = requireRecord(value, path)
  return new THREE.Vector3(
    readFiniteNumber(record.x, `${path}.x`),
    readFiniteNumber(record.y, `${path}.y`),
    readFiniteNumber(record.z, `${path}.z`)
  )
}

function readFiniteNumber(value: unknown, path: string): number {
  if (typeof value !== 'number' || !Number.isFinite(value)) {
    throw new GSplatEffectTemplateError(`${path} must be a finite number`)
  }
  return value
}

function readOptionalBox3(value: unknown, path: string): THREE.Box3 | null {
  if (value === undefined || value === null) {
    return null
  }

  const record = requireRecord(value, path)
  const min = readVector3(record.min, `${path}.min`)
  const max = readVector3(record.max, `${path}.max`)
  if (max.x < min.x || max.y < min.y || max.z < min.z) {
    throw new GSplatEffectTemplateError(`${path} must not be empty`)
  }
  return new THREE.Box3(min, max)
}

function readOptionalPositiveNumber(value: unknown, path: string): number | null {
  if (value === undefined || value === null) {
    return null
  }
  return readPositiveNumber(value, path)
}

function readPositiveNumber(value: unknown, path: string, fallback?: number): number {
  if (value === undefined) {
    if (fallback === undefined) {
      throw new GSplatEffectTemplateError(`${path} must be a positive finite number`)
    }
    return fallback
  }
  if (typeof value !== 'number' || !Number.isFinite(value) || value <= 0) {
    throw new GSplatEffectTemplateError(`${path} must be a positive finite number`)
  }
  return value
}

function readCameraContext(value: unknown): NormalizedRuntimeContext['camera'] {
  const record = requireRecord(value, 'runtime.camera')
  const position = readVector3(record.position, 'runtime.camera.position')
  const target = readVector3(record.target, 'runtime.camera.target')
  const up = readVector3(record.up, 'runtime.camera.up')

  if (up.lengthSq() <= EPSILON) {
    throw new GSplatEffectTemplateError('runtime.camera.up must not be a zero vector')
  }
  if (position.distanceToSquared(target) <= EPSILON) {
    throw new GSplatEffectTemplateError('runtime.camera.position must not equal target')
  }

  return { position, target, up: up.normalize() }
}

function createTargetBox(center: THREE.Vector3, size: number): THREE.Box3 {
  const halfSize = size * 0.5
  return new THREE.Box3(
    new THREE.Vector3(center.x - halfSize, center.y - halfSize, center.z - halfSize),
    new THREE.Vector3(center.x + halfSize, center.y + halfSize, center.z + halfSize)
  )
}

function createSweepBox(targetBox: THREE.Box3, bounds: THREE.Box3 | null): THREE.Box3 {
  if (!bounds || bounds.isEmpty()) {
    return targetBox.clone()
  }

  const sweepBox = targetBox.clone().intersect(bounds)
  return sweepBox.isEmpty() ? targetBox.clone() : sweepBox
}

function createBoxDirection(
  direction: GSplatEffectTemplateBoxDirection,
  reverse: boolean
): THREE.Vector3 {
  const [x, y, z] = BOX_DIRECTION_VECTORS[direction]
  const sign = reverse ? -1 : 1
  return new THREE.Vector3(x * sign, y * sign, z * sign)
}

function shouldReverseBoxScan(effectKey: GSplatEffectTemplateBoxScanKey): boolean {
  return effectKey === 'box-hide' || effectKey === 'box-untint'
}

function resolveSceneRadius(runtime: NormalizedRuntimeContext): {
  radius: number
  usedEffectBounds: boolean
  usedSceneBounds: boolean
} {
  if (runtime.sceneRadius !== null) {
    return {
      radius: clampRevealRadius(runtime.sceneRadius),
      usedEffectBounds: false,
      usedSceneBounds: false,
    }
  }

  const bounds = runtime.effectBounds ?? runtime.sceneBounds
  if (bounds && !bounds.isEmpty()) {
    const sphere = bounds.getBoundingSphere(new THREE.Sphere())
    return {
      radius: clampRevealRadius(
        Math.max(EPSILON, runtime.center.distanceTo(sphere.center) + sphere.radius)
      ),
      usedEffectBounds: runtime.effectBounds !== null,
      usedSceneBounds: runtime.effectBounds === null && runtime.sceneBounds !== null,
    }
  }

  return {
    radius: clampRevealRadius(Math.max(runtime.minRevealRadius, runtime.defaultSceneRadius)),
    usedEffectBounds: false,
    usedSceneBounds: false,
  }
}

function clampRevealRadius(radius: number): number {
  return Math.min(MAX_REVEAL_RADIUS, radius)
}

function createColor(hex: string, intensity = 1): THREE.Color {
  return new THREE.Color(hex).multiplyScalar(intensity)
}

function createColorControlColor(control: GSplatEffectTemplateColorControl): THREE.Color {
  return createColor(control.color, control.intensity)
}

function toTuple(value: THREE.Vector3): [number, number, number] {
  return [sanitizeZero(value.x), sanitizeZero(value.y), sanitizeZero(value.z)]
}

interface CameraAdaptiveObbOptions {
  cameraPosition: THREE.Vector3
  cameraTarget: THREE.Vector3
  up: THREE.Vector3
  bounds: THREE.Box3 | null
}

interface CameraAdaptiveObbParameters {
  origin: [number, number, number]
  axisX: [number, number, number]
  axisY: [number, number, number]
  axisZ: [number, number, number]
  min: [number, number, number]
  max: [number, number, number]
  direction: [number, number, number]
  sweepStart: number
  sweepEnd: number
}

function createCameraAdaptiveObb(options: CameraAdaptiveObbOptions): CameraAdaptiveObbParameters {
  const up = normalizeOrFallback(options.up, DEFAULT_UP)
  const forward = computeHorizontalForward(options.cameraPosition, options.cameraTarget, up)
  const axisY = forward
  const axisX = normalizeOrFallback(
    new THREE.Vector3().crossVectors(axisY, up),
    new THREE.Vector3(1, 0, 0)
  )
  const axisZ = normalizeOrFallback(new THREE.Vector3().crossVectors(axisX, axisY), up)
  const origin = options.cameraPosition.clone()
  const localBounds = projectBoundsToObb(options.bounds, origin, axisX, axisY, axisZ)
  const fallbackSweepLength = getCameraAdaptiveObbFallbackSweepLength(options.bounds)
  const fallbackHalfExtent = fallbackSweepLength * 0.5
  const rawForwardLength = Math.max(0, localBounds.max.y)
  const sweepLength =
    rawForwardLength > fallbackSweepLength * 0.1 ? rawForwardLength : fallbackSweepLength

  const minY = 0
  const maxY = Math.max(sweepLength, localBounds.max.y, 0.001)
  const min = new THREE.Vector3(
    Math.min(localBounds.min.x, -fallbackHalfExtent),
    minY,
    Math.min(localBounds.min.z, -fallbackHalfExtent)
  )
  const max = new THREE.Vector3(
    Math.max(localBounds.max.x, fallbackHalfExtent),
    maxY,
    Math.max(localBounds.max.z, fallbackHalfExtent)
  )
  const sweepStart = 0
  const sweepEnd = sweepStart + sweepLength

  return {
    origin: toTuple(origin),
    axisX: toTuple(axisX),
    axisY: toTuple(axisY),
    axisZ: toTuple(axisZ),
    min: toTuple(min),
    max: toTuple(max),
    direction: [0, 1, 0],
    sweepStart,
    sweepEnd,
  }
}

function getCameraAdaptiveObbFallbackSweepLength(bounds: THREE.Box3 | null): number {
  if (!bounds || bounds.isEmpty()) {
    return 0.001
  }
  const sphere = bounds.getBoundingSphere(new THREE.Sphere())
  return Math.max(0.001, sphere.radius * 2)
}

function computeHorizontalForward(
  cameraPosition: THREE.Vector3,
  cameraTarget: THREE.Vector3,
  up: THREE.Vector3
): THREE.Vector3 {
  const forward = cameraTarget.clone().sub(cameraPosition)
  forward.addScaledVector(up, -forward.dot(up))
  return normalizeOrFallback(forward, DEFAULT_FORWARD)
}

function normalizeOrFallback(
  value: THREE.Vector3 | undefined,
  fallback: THREE.Vector3
): THREE.Vector3 {
  if (!value || value.lengthSq() < EPSILON) {
    return fallback.clone().normalize()
  }
  return value.clone().normalize()
}

function projectBoundsToObb(
  bounds: THREE.Box3 | null,
  origin: THREE.Vector3,
  axisX: THREE.Vector3,
  axisY: THREE.Vector3,
  axisZ: THREE.Vector3
): THREE.Box3 {
  if (!bounds || bounds.isEmpty()) {
    return new THREE.Box3(new THREE.Vector3(-0.5, 0, -0.5), new THREE.Vector3(0.5, 1, 0.5))
  }

  const localBounds = new THREE.Box3()
  for (const corner of getBoxCorners(bounds)) {
    const delta = corner.sub(origin)
    localBounds.expandByPoint(
      new THREE.Vector3(delta.dot(axisX), delta.dot(axisY), delta.dot(axisZ))
    )
  }
  return localBounds
}

function getBoxCorners(box: THREE.Box3): THREE.Vector3[] {
  const { min, max } = box
  return [
    new THREE.Vector3(min.x, min.y, min.z),
    new THREE.Vector3(min.x, min.y, max.z),
    new THREE.Vector3(min.x, max.y, min.z),
    new THREE.Vector3(min.x, max.y, max.z),
    new THREE.Vector3(max.x, min.y, min.z),
    new THREE.Vector3(max.x, min.y, max.z),
    new THREE.Vector3(max.x, max.y, min.z),
    new THREE.Vector3(max.x, max.y, max.z),
  ]
}

function sanitizeZero(value: number): number {
  return Math.abs(value) < 1e-12 ? 0 : value
}
